export interface ActionItemTextSource {
	displayText?: unknown;
	requirementText?: unknown;
	question?: unknown;
	messageText?: unknown;
	text?: unknown;
	label?: unknown;
}

const stringValue = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const textValue = (value: unknown): string => stringValue(value).replace(/\s+/g, ' ').trim();

const LEGACY_LABELS = [
	'Required scope',
	'Bid form answer',
	'Written quote disposition',
	'Written quote evidence',
	'Written quote cost',
	'Conflict kind',
];

const legacyLabelPattern = LEGACY_LABELS.join('|');

const legacyValue = (value: string, label: string): string => {
	const match = value.match(
		new RegExp(`\\b${label}\\s*:\\s*([\\s\\S]*?)(?=\\s*\\b(?:${legacyLabelPattern})\\s*:|$)`, 'i'),
	);
	return textValue(match?.[1] || '');
};

const sentence = (value: string): string => {
	const trimmed = textValue(value).replace(/\s*\.{2,}\s*$/, '.');
	if (!trimmed) return '';
	return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const lower = (value: string): string =>
	value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : value;

/**
 * Older action-item runs stored an audit serialization in the user-facing text
 * field. Keep those records readable without exposing internal enum names or a
 * repeated proposal total. New records should provide displayText directly.
 */
export const cleanLegacyActionItemText = (value: unknown): string => {
	const original = stringValue(value);
	const raw = textValue(original);
	if (!raw) return '';
	const hasLegacyLabels = new RegExp(`\\b(?:${legacyLabelPattern})\\s*:`, 'i').test(raw);
	if (!hasLegacyLabels) {
		return stringValue(original.replace(/\s*\bConflict kind\s*:\s*[A-Z][A-Z0-9_]*\s*\.?\s*$/i, ''));
	}

	const auditText = raw.replace(
		/^Please clarify this bidder-confirmed exclusion or identified conflict for the required scope:\s*/i,
		'',
	);
	const requirement = legacyValue(auditText, 'Required scope');
	const formAnswer = legacyValue(auditText, 'Bid form answer')
		.replace(/[.\s]+$/, '')
		.toUpperCase();
	const disposition = legacyValue(auditText, 'Written quote disposition')
		.replace(/[.\s]+$/, '')
		.toUpperCase();
	const evidence = legacyValue(auditText, 'Written quote evidence')
		.replace(/^\s*(?:conflict|evidence)\s*:\s*/i, '')
		.replace(/\s*\.+\s*$/, '');
	const parts: string[] = [];

	if (requirement) parts.push(sentence(requirement));

	if (evidence) {
		const lead =
			formAnswer === 'YES'
				? 'The bid form marks this scope included, but the quote indicates'
				: 'The quote indicates';
		parts.push(sentence(`${lead}: ${lower(evidence)}`));
	} else if (formAnswer === 'NO') {
		parts.push('The bidder marked this required scope as excluded.');
	} else if (['PARTIAL', 'DISCREPANCY', 'DEFICIENCY'].includes(disposition)) {
		parts.push('The quote appears to include only part of the required scope.');
	} else if (['NO', 'EXCLUDED', 'CONTRADICTION'].includes(disposition)) {
		parts.push('The quote appears to exclude this required scope.');
	}

	if (requirement || evidence) {
		parts.push(
			formAnswer === 'NO' || ['NO', 'EXCLUDED', 'CONTRADICTION'].includes(disposition)
				? 'Please confirm whether it will be included in the base bid.'
				: 'Please confirm the complete required scope is included in the base bid.',
		);
		return textValue(parts.join(' '));
	}

	// Defensive fallback for unusual historical records: remove audit-only
	// fields instead of presenting their internal labels to the user.
	return textValue(
		raw
			.replace(
				/^Please clarify this bidder-confirmed exclusion or identified conflict for the required scope:\s*/i,
				'',
			)
			.replace(/\s*\bWritten quote cost\s*:[\s\S]*?(?=\s*\bConflict kind\s*:|$)/i, ' ')
			.replace(/\s*\bConflict kind\s*:\s*[A-Z][A-Z0-9_]*\s*\.?/i, ' ')
			.replace(/\b(?:Bid form answer|Written quote disposition|Written quote evidence)\s*:/gi, ''),
	);
};

const firstText = (values: unknown[]): string => {
	for (const value of values) {
		const candidate = stringValue(value);
		if (candidate) return candidate;
	}
	return '';
};

export const actionItemDisplayText = (
	item: ActionItemTextSource | null | undefined,
	fallback = 'Review this action item.',
): string => {
	if (!item) return fallback;
	const value = firstText([
		item.displayText,
		item.requirementText,
		item.text,
		item.question,
		item.messageText,
		item.label,
	]);
	return cleanLegacyActionItemText(value) || fallback;
};

export const actionItemQuestionText = (
	item: ActionItemTextSource | null | undefined,
	fallback = 'Please clarify this item.',
): string => {
	if (!item) return fallback;
	// messageText is an explicit user edit and must continue to win over the
	// generated display copy when an approval card is being edited.
	const value = firstText([
		item.messageText,
		item.question,
		item.displayText,
		item.requirementText,
		item.text,
		item.label,
	]);
	return cleanLegacyActionItemText(value) || fallback;
};
